/**
 * Designer document state with undo / redo.
 *
 * `historyReducer` is the whole of it — a plain past / present / future stack —
 * and `useDesignerHistory` wraps it for React. Hosts own one of these and hand
 * `present` to `DesignerWorkspace`.
 *
 * Coalescing: a commit may carry a key (say `section-3:width`). Commits with the
 * same key arriving within a short window replace the present instead of adding
 * a step, so nudging a number field ten times is one undo, not ten.
 */

import { useCallback, useMemo, useReducer } from 'react';

export const HISTORY_LIMIT = 100;
export const COALESCE_MS = 800;

export interface History<T> {
  past: T[];
  present: T;
  future: T[];
  /** Key and time of the commit that produced `present`, for coalescing. */
  lastKey: string | null;
  lastAt: number;
}

export type HistoryAction<T> =
  | { type: 'commit'; value: T; key?: string; at: number }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'reset'; value: T };

export function initialHistory<T>(present: T): History<T> {
  return { past: [], present, future: [], lastKey: null, lastAt: 0 };
}

export function historyReducer<T>(state: History<T>, action: HistoryAction<T>): History<T> {
  switch (action.type) {
    case 'commit': {
      if (action.value === state.present) return state;
      const coalesce = action.key !== undefined
        && action.key === state.lastKey
        && action.at - state.lastAt <= COALESCE_MS
        && state.past.length > 0;
      if (coalesce) {
        return { ...state, present: action.value, future: [], lastAt: action.at };
      }
      return {
        past: [...state.past, state.present].slice(-HISTORY_LIMIT),
        present: action.value,
        future: [],
        lastKey: action.key ?? null,
        lastAt: action.at,
      };
    }
    case 'undo': {
      if (state.past.length === 0) return state;
      return {
        past: state.past.slice(0, -1),
        present: state.past[state.past.length - 1],
        future: [state.present, ...state.future],
        lastKey: null,
        lastAt: 0,
      };
    }
    case 'redo': {
      if (state.future.length === 0) return state;
      return {
        past: [...state.past, state.present],
        present: state.future[0],
        future: state.future.slice(1),
        lastKey: null,
        lastAt: 0,
      };
    }
    case 'reset':
      return initialHistory(action.value);
    default:
      return state;
  }
}

export interface DesignerHistory<T> {
  present: T;
  commit: (value: T, key?: string) => void;
  undo: () => void;
  redo: () => void;
  reset: (value: T) => void;
  canUndo: boolean;
  canRedo: boolean;
}

export function useDesignerHistory<T>(initial: T | (() => T)): DesignerHistory<T> {
  const [state, dispatch] = useReducer(
    historyReducer as (s: History<T>, a: HistoryAction<T>) => History<T>,
    undefined,
    () => initialHistory(typeof initial === 'function' ? (initial as () => T)() : initial),
  );

  const commit = useCallback((value: T, key?: string) => dispatch({ type: 'commit', value, key, at: Date.now() }), []);
  const undo = useCallback(() => dispatch({ type: 'undo' }), []);
  const redo = useCallback(() => dispatch({ type: 'redo' }), []);
  const reset = useCallback((value: T) => dispatch({ type: 'reset', value }), []);

  return useMemo(() => ({
    present: state.present,
    commit,
    undo,
    redo,
    reset,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
  }), [state, commit, undo, redo, reset]);
}
